import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { fetchAudienceMetadata } from 'Features/audience/stores/thunks/audience-group.thunks';
import { useAppDispatch } from 'Libs/redux/store';
import { useTypedSelector } from 'Libs/redux/utils';
import { AudienceForm, type AudienceModes } from 'Pages/Activity/AudienceForm/AudienceForm';
import { type CampaignFormValues } from 'Pages/Activity/Campaign/saveCampaign.hook';
import { getLanguagesFromCode } from 'Services/language/languageService';
import { selectCampaignMagicCode } from 'Store/entities/activity/activity.selectors';

export const Audience: FC = () => {
  const dispatch = useAppDispatch();
  const { campaign } = useTypedSelector((state) => state.entities.activity.edition);

  const { watch, setValue } = useFormContext<CampaignFormValues>();
  const originalMagicCode = useSelector(selectCampaignMagicCode);

  const updateSegmentation = useCallback((value: number[]) => {
    setValue('audiences.segmentations', value);
    setValue('audienceDirty', true);
  }, []);

  const updateMagicCode = useCallback((value: string) => {
    setValue('audiences.magicCode', value);
    setValue('audienceDirty', true);
  }, []);

  const updateEmailList = useCallback((value: { addedEmails: string[]; removedEmails: string[] }) => {
    setValue('audiences.emailList', value);
    setValue('audienceDirty', true);
  }, []);

  const resetAudience = useCallback(
    (mode: AudienceModes) => {
      const isMagicCode = mode === 'MAGIC_CODE';
      const isEmailList = mode === 'EMAIL_LIST';
      const isSegmentation = mode === 'SEGMENTATION';
      const magicCode = watch('audiences.magicCode');
      const emailList = watch('audiences.emailList');
      const segmentations = watch('audiences.segmentations');

      if ((isMagicCode && magicCode) || (isEmailList && emailList) || (isSegmentation && segmentations)) {
        return;
      }

      const currentAudienceType = campaign?.audiences?.[0]?.type;
      const isCurrentMagicCode = currentAudienceType === 'magic-code';
      const isCurrentEmailList = currentAudienceType === 'email-list';
      const isCurrentSegmentation = currentAudienceType === 'segmentation';

      if (
        (isMagicCode && !isCurrentMagicCode) ||
        (isEmailList && !isCurrentEmailList) ||
        (isSegmentation && !isCurrentSegmentation)
      ) {
        setValue('audienceTypeChanged', true);
      }

      setValue('audienceDirty', true);
      setValue('audiences.emailList', isEmailList ? { addedEmails: [], removedEmails: [] } : undefined);
      setValue('audiences.magicCode', undefined);
      setValue('audiences.segmentations', undefined);
      setValue('audiences.campaign', undefined);
    },
    [campaign?.audiences],
  );

  if (!campaign) {
    return null;
  }

  const languages = useMemo(() => getLanguagesFromCode(campaign.languages), [campaign.languages]);

  useEffect(() => {
    dispatch(
      fetchAudienceMetadata({
        groupIds: campaign?.audiences?.map((audience) => audience.groupId) || [],
        languageIds: languages.map((language) => language.availableGroupId!),
      }),
    );
  }, [campaign?.audiences]);

  return (
    <div>
      <AudienceForm
        entityType="activity"
        scope="campaign"
        entityId={campaign.id}
        assignedAudiences={campaign?.audiences || []}
        languages={languages}
        magicCode={{
          originalValue: originalMagicCode,
          value: watch('audiences.magicCode'),
          update: updateMagicCode,
          hasAudienceTypeChanged: watch('audienceTypeChanged'),
        }}
        emailList={{
          value: watch('audiences.emailList'),
          update: updateEmailList,
          hasAudienceTypeChanged: watch('audienceTypeChanged'),
        }}
        segmentation={{
          value: watch('audiences.segmentations'),
          update: updateSegmentation,
        }}
        campaign={{
          hasAudienceTypeChanged: watch('audienceTypeChanged'),
          originalValue: campaign?.audiences?.[0]?.type,
          value: watch('audiences.campaign.targetedId'),
          update: (value: number) => setValue('audiences.campaign.targetedId', value),
        }}
        resetFormValues={resetAudience}
      />
    </div>
  );
};
